import "./style.css";
import { Net, creaPartita, esiste, playerId } from "./net";
import { renderStats } from "./stats";
import { posizioni, verdetto } from "./esito";
import type { DomandaView, GiocatoreView, OutMsg, RiepilogoRound, Risposta } from "../../worker/src/protocol";

const app = document.getElementById("app")!;

interface Sessione {
  codice: string;
  nome: string;
  host: boolean;
}

interface Stato {
  schermo: "home" | "lobby" | "domanda" | "reveal" | "finale" | "stats";
  sessione: Sessione | null;
  connesso: boolean;
  giocatori: GiocatoreView[];
  domande: number;
  d: DomandaView | null;
  hanno_risposto: string[];
  mia: Risposta | null;
  inviata: boolean;
  reveal: Extract<OutMsg, { t: "reveal" }> | null;
  finale: Extract<OutMsg, { t: "finale" }> | null;
  stats: { dati: unknown; domande: RiepilogoRound[] } | null;
  errore: string;
}

const S: Stato = {
  schermo: "home",
  sessione: null,
  connesso: false,
  giocatori: [],
  domande: 20,
  d: null,
  hanno_risposto: [],
  mia: null,
  inviata: false,
  reveal: null,
  finale: null,
  stats: null,
  errore: "",
};

let net: Net | null = null;
const io = playerId();

// --- utilita' ---------------------------------------------------------------

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

function on(sel: string, ev: string, fn: (e: Event, el: HTMLElement) => void) {
  app.querySelectorAll<HTMLElement>(sel).forEach((el) => el.addEventListener(ev, (e) => fn(e, el)));
}

function salvaSessione(s: Sessione | null) {
  if (s) localStorage.setItem("sessione", JSON.stringify(s));
  else localStorage.removeItem("sessione");
}

// --- connessione ------------------------------------------------------------

function collega(s: Sessione) {
  net?.chiudi();
  S.sessione = s;
  salvaSessione(s);
  net = new Net(s.codice, s.nome, s.host, ricevi, (c) => {
    S.connesso = c;
    render();
  });
  net.apri();
}

function ricevi(m: OutMsg) {
  switch (m.t) {
    case "benvenuto":
      if (S.sessione) S.sessione.host = m.host;
      S.errore = "";
      if (S.schermo === "home") S.schermo = "lobby";
      break;
    case "lobby":
      S.giocatori = m.giocatori;
      S.domande = m.domande;
      if (m.fase === "lobby" && S.schermo !== "home") S.schermo = "lobby";
      break;
    case "domanda":
      S.d = m.d;
      S.hanno_risposto = m.hanno_risposto;
      S.mia = null;
      S.inviata = m.hanno_risposto.includes(io);
      S.reveal = null;
      S.schermo = "domanda";
      break;
    case "hanno_risposto":
      S.hanno_risposto = m.ids;
      break;
    case "reveal":
      S.reveal = m;
      S.schermo = "reveal";
      break;
    case "finale":
      S.finale = m;
      S.schermo = "finale";
      break;
    case "stats":
      S.stats = { dati: m.dati, domande: m.domande };
      S.schermo = "stats";
      break;
    case "errore":
      S.errore = m.msg;
      break;
  }
  render();
}

// --- schermate --------------------------------------------------------------

function home(): string {
  return `
    <h1>Drago Quiz</h1>
    <p>Otto anni di gruppo, venti domande. Una squadra per telefono.</p>
    <div class="pila" style="margin-top:24px">
      <button class="primario" id="crea">Crea la partita</button>
      <div class="card pila">
        <input class="codice" id="codice" maxlength="4" placeholder="CODE" autocomplete="off" inputmode="latin" />
        <input id="nome" maxlength="24" placeholder="Nome della coppia" autocomplete="off" />
        <button id="entra">Entra</button>
      </div>
      ${S.errore ? `<p style="color:var(--ko)">${esc(S.errore)}</p>` : ""}
    </div>`;
}

/** Barra sempre presente a partita aperta: si esce da qualsiasi schermata, non
 * solo dalla fine. Senza, l'host resta incollato alla stanza vecchia. */
function barra(): string {
  const s = S.sessione!;
  return `<div class="riga" style="justify-content:space-between;margin-bottom:12px">
    <small>Partita <b>${esc(s.codice)}</b>${s.host ? " · host" : ""}</small>
    <button class="fantasma" id="esci" style="width:auto;min-height:36px;padding:6px 12px">Esci</button>
  </div>`;
}

function lobby(): string {
  const s = S.sessione!;
  const coppie = S.giocatori.filter((g) => !g.host);
  return `
    <h1>Codice partita</h1>
    <div class="card"><div class="codice-grande">${esc(s.codice)}</div>
    <p style="text-align:center;margin:0">Gli altri telefoni entrano con questo codice.</p></div>
    <h2>Coppie in gioco (${coppie.length}/5)</h2>
    <div class="classifica">
      ${coppie
        .map(
          (g) => `<div class="pos ${g.id === io ? "io" : ""}"><span class="pallino ${g.connesso ? "" : "giu"}"></span>
          <span class="nome">${esc(g.nome)}</span></div>`,
        )
        .join("")}
      ${coppie.length === 0 ? `<p>Nessuno ancora.</p>` : ""}
    </div>
    ${
      s.host
        ? `<div class="pila" style="margin-top:20px">
             <button class="primario" id="start" ${coppie.length < 2 ? "disabled" : ""}>
               Comincia (${S.domande} domande)</button>
             ${coppie.length < 2 ? `<small>Servono almeno due coppie.</small>` : ""}
           </div>`
        : `<p style="margin-top:20px">Si parte quando l'host preme il tasto.</p>`
    }
    ${S.errore ? `<p style="color:var(--ko)">${esc(S.errore)}</p>` : ""}`;
}

function opzioniHtml(d: DomandaView, mia: Risposta | null, rev: Stato["reveal"]): string {
  const scelte = Array.isArray(mia) ? mia : mia ? [mia] : [];
  return d.opzioni
    .map((o) => {
      const pos = scelte.indexOf(o);
      let cls = "opzione";
      if (rev) {
        const v = verdetto(d.tipo, o, rev.risposta, mia);
        if (v) cls += " " + v;
      } else if (pos >= 0) cls += " scelta";
      let badge = "";
      if (d.tipo === "ordina-3") {
        if (rev) {
          // al reveal il pallino mostra l'ordine giusto, accanto quello dato
          const { giusta, tua } = posizioni(o, rev.risposta, mia);
          badge = `<span class="riga" style="gap:6px">
            ${tua && tua !== giusta ? `<small>tuo: ${tua}°</small>` : ""}
            <span class="ordine">${giusta ?? "-"}</span></span>`;
        } else if (pos >= 0) {
          badge = `<span class="ordine">${pos + 1}</span>`;
        }
      }
      return `<button class="${cls}" data-op="${esc(o)}" ${rev || S.inviata ? "disabled" : ""}>
        <span>${esc(o)}</span>${badge}</button>`;
    })
    .join("");
}

function domanda(): string {
  const d = S.d!;
  const host = S.sessione!.host;
  const risposte = S.hanno_risposto.length;
  const coppie = S.giocatori.filter((g) => !g.host).length;
  return `
    <div class="riga" style="justify-content:space-between">
      <small>Domanda ${d.idx}/${d.totale} · ${esc(d.categoria)}</small>
      <small>${d.peso} punti</small>
    </div>
    <div class="timer" id="timer"><i style="width:100%"></i></div>
    <h1 style="margin-top:16px;font-size:1.35rem">${esc(d.domanda)}</h1>
    ${d.frase ? `<div class="card frase">"${esc(d.frase)}"</div>` : ""}
    ${d.tipo === "ordina-3" ? `<small>Tocca nell'ordine giusto: primo, secondo, terzo.</small>` : ""}
    ${host ? `<p>Hanno risposto ${risposte} coppie su ${coppie}.</p>` : ""}
    <div class="opzioni">${opzioniHtml(d, S.mia, null)}</div>
    ${S.inviata ? `<p>Risposta presa. Si aspetta gli altri.</p>` : ""}
    ${host ? `<div class="pila" style="margin-top:16px"><button id="next" class="fantasma">Chiudi il round</button></div>` : ""}`;
}

function reveal(): string {
  const r = S.reveal!;
  const d = S.d!;
  const mio = r.esiti.find((e) => e.id === io);
  return `
    <small>Domanda ${d.idx}/${d.totale}</small>
    <h1 style="font-size:1.3rem">${esc(d.domanda)}</h1>
    ${d.frase ? `<div class="card frase">"${esc(d.frase)}"</div>` : ""}
    <div class="opzioni">${opzioniHtml(d, mio?.risposta ?? null, r)}</div>
    <div class="card" style="margin-top:16px"><p style="margin:0">${esc(r.reveal)}</p></div>
    ${mio ? `<h2>${mio.punti > 0 ? `+${mio.punti} punti` : "Niente punti"}</h2>` : ""}
    <h2>Classifica</h2>
    <div class="classifica">
      ${r.classifica
        .map((g, i) => {
          const e = r.esiti.find((x) => x.id === g.id);
          return `<div class="pos ${g.id === io ? "io" : ""}"><span class="num">${i + 1}</span>
            <span class="nome">${esc(g.nome)}</span>
            <span class="delta">${e && e.punti > 0 ? `+${e.punti}` : ""}</span>
            <span class="punti">${g.punti}</span></div>`;
        })
        .join("")}
    </div>
    ${
      S.sessione!.host
        ? `<div class="pila" style="margin-top:20px"><button class="primario" id="next">
             ${r.ultima ? "Classifica finale" : "Prossima domanda"}</button></div>`
        : `<p style="margin-top:20px">L'host manda avanti.</p>`
    }`;
}

function finale(): string {
  const f = S.finale!;
  const podio = f.classifica;
  return `
    <h1>Fine partita</h1>
    <div class="classifica">
      ${podio
        .map(
          (g, i) => `<div class="pos ${g.id === io ? "io" : ""}">
            <span class="num">${["🥇", "🥈", "🥉"][i] ?? i + 1}</span>
            <span class="nome">${esc(g.nome)}</span><span class="punti">${g.punti}</span></div>`,
        )
        .join("")}
    </div>
    <div class="pila" style="margin-top:24px">
      <button class="primario" id="stats">Statistiche del gruppo</button>
      ${S.sessione!.host ? `<button id="rigioca">Un'altra partita, stesse coppie</button>` : ""}
    </div>`;
}

/** Stacca il telefono dalla stanza: websocket chiuso, sessione dimenticata,
 * si torna alla home. Il `playerId` resta, serve per rientrare altrove. */
function esci() {
  const inCorso = S.schermo === "domanda" || S.schermo === "reveal";
  if (inCorso && !confirm("Uscire dalla partita in corso?")) return;
  net?.chiudi();
  net = null;
  salvaSessione(null);
  S.schermo = "home";
  S.sessione = null;
  S.giocatori = [];
  S.d = null;
  S.mia = null;
  S.inviata = false;
  S.hanno_risposto = [];
  S.reveal = null;
  S.finale = null;
  S.stats = null;
  S.errore = "";
  S.connesso = false;
  render();
}

function render() {
  // dopo qualche tentativo a vuoto il problema non e' la rete: di solito e' la
  // sessione Access scaduta, e si sblocca solo ricaricando (il login e' una
  // pagina, non una risposta JSON)
  const badge = S.connesso
    ? ""
    : `<div class="stato">${
        (net?.tentativi ?? 0) >= 4 ? "connessione persa · ricarica la pagina" : "offline, rientro in corso…"
      }</div>`;
  /** dentro una partita ogni schermata ha la barra con codice e uscita */
  const dipingi = (html: string) => {
    app.innerHTML = barra() + html + badge;
    on("#esci", "click", esci);
  };
  switch (S.schermo) {
    case "home":
      app.innerHTML = home();
      on("#crea", "click", async () => {
        try {
          const codice = await creaPartita();
          collega({ codice, nome: "Host", host: true });
        } catch (e) {
          S.errore =
            (e as Error).message === "ACCESS"
              ? "Sessione scaduta: ricarica la pagina per rifare il login"
              : "Server non raggiungibile";
          render();
        }
      });
      on("#entra", "click", async () => {
        const codice = (app.querySelector<HTMLInputElement>("#codice")!.value || "").toUpperCase().trim();
        const nome = app.querySelector<HTMLInputElement>("#nome")!.value.trim();
        if (!/^[A-Z]{4}$/.test(codice)) return alert("Il codice e' di quattro lettere");
        if (!nome) return alert("Serve un nome per la coppia");
        try {
          if (!(await esiste(codice))) return alert("Nessuna partita con questo codice");
        } catch (e) {
          return alert(
            (e as Error).message === "ACCESS"
              ? "Sessione scaduta: ricarica la pagina per rifare il login"
              : "Server non raggiungibile",
          );
        }
        collega({ codice, nome, host: false });
      });
      return;
    case "lobby":
      dipingi(lobby());
      on("#start", "click", () => net?.manda({ t: "start" }));
      return;
    case "domanda": {
      dipingi(domanda());
      on(".opzione", "click", (_e, el) => scegli(el.dataset.op!));
      on("#next", "click", () => net?.manda({ t: "next" }));
      avviaTimer();
      return;
    }
    case "reveal":
      dipingi(reveal());
      on("#next", "click", () => net?.manda({ t: "next" }));
      return;
    case "finale":
      dipingi(finale());
      on("#stats", "click", () => net?.manda({ t: "stats" }));
      on("#rigioca", "click", () => net?.manda({ t: "start" }));
      return;
    case "stats":
      app.innerHTML = `<div id="stats-root"></div>`;
      renderStats(app.querySelector<HTMLElement>("#stats-root")!, S.stats!.dati, S.stats!.domande, io);
      on("#indietro", "click", () => {
        S.schermo = "finale";
        render();
      });
      return;
  }
}

// --- risposta ---------------------------------------------------------------

function scegli(op: string) {
  const d = S.d;
  if (!d || S.inviata || S.sessione!.host) return;
  if (d.tipo === "ordina-3") {
    const cur = Array.isArray(S.mia) ? [...S.mia] : [];
    const i = cur.indexOf(op);
    if (i >= 0) cur.splice(i, 1);
    else if (cur.length < 3) cur.push(op);
    S.mia = cur;
    if (cur.length === 3) {
      S.inviata = true;
      net?.manda({ t: "answer", qid: d.qid, valore: cur });
    }
    render();
    return;
  }
  S.mia = op;
  S.inviata = true;
  net?.manda({ t: "answer", qid: d.qid, valore: op });
  render();
}

// --- timer ------------------------------------------------------------------

let tick: ReturnType<typeof setInterval> | null = null;

function avviaTimer() {
  if (tick) clearInterval(tick);
  const barra = app.querySelector<HTMLElement>("#timer");
  const i = barra?.querySelector("i") as HTMLElement | null;
  if (!barra || !i || !S.d) return;
  const d = S.d;
  const durata = d.scadenza - d.adesso;
  const aggiorna = () => {
    const resta = d.scadenza - (Date.now() + (net?.scarto ?? 0));
    const q = Math.max(0, Math.min(1, resta / durata));
    i.style.width = (q * 100).toFixed(1) + "%";
    barra.classList.toggle("rosso", q < 0.25);
    if (q <= 0 && tick) {
      clearInterval(tick);
      tick = null;
    }
  };
  aggiorna();
  tick = setInterval(aggiorna, 100);
}

// --- avvio ------------------------------------------------------------------

const salvata = localStorage.getItem("sessione");
if (salvata) {
  try {
    const s = JSON.parse(salvata) as Sessione;
    // se la stanza non c'e' piu' (TTL scaduto) si dimentica, altrimenti il
    // telefono resterebbe a riconnettersi a vuoto per sempre
    esiste(s.codice).then(
      (ok) => (ok ? collega(s) : (salvaSessione(null), render())),
      // se non si riesce a chiedere (Access, rete), la sessione si tiene
      () => collega(s),
    );
  } catch {
    salvaSessione(null);
  }
}
render();
